import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  BadgeCheck,
  Award,
  X,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { useCollectionData } from "../hooks/useCollectionData";
import { useSectionVisibility } from "../hooks/useSectionVisibility";
import { FALLBACK_CERTIFICATES } from "../data/content";

const getCertImage = (item) => {
  if (item.imageRef?.content) return item.imageRef.content;
  if (item.imageRef) return item.imageRef;
  return "/images/certificate/certificate-1.jpeg";
};

const cornerOrnament = (position) => {
  const config = {
    "top-left": {
      container: "top-0 left-0 w-6 h-6 sm:w-8 sm:h-8",
      horiz: "top-0 left-0 w-full h-[2px] bg-gradient-to-r from-[#d4af37] to-transparent",
      vert: "top-0 left-0 h-full w-[2px] bg-gradient-to-b from-[#d4af37] to-transparent",
    },
    "top-right": {
      container: "top-0 right-0 w-6 h-6 sm:w-8 sm:h-8",
      horiz: "top-0 right-0 w-full h-[2px] bg-gradient-to-l from-[#d4af37] to-transparent",
      vert: "top-0 right-0 h-full w-[2px] bg-gradient-to-b from-[#d4af37] to-transparent",
    },
    "bottom-left": {
      container: "bottom-0 left-0 w-6 h-6 sm:w-8 sm:h-8",
      horiz: "bottom-0 left-0 w-full h-[2px] bg-gradient-to-r from-[#d4af37] to-transparent",
      vert: "bottom-0 left-0 h-full w-[2px] bg-gradient-to-t from-[#d4af37] to-transparent",
    },
    "bottom-right": {
      container: "bottom-0 right-0 w-6 h-6 sm:w-8 sm:h-8",
      horiz: "bottom-0 right-0 w-full h-[2px] bg-gradient-to-l from-[#d4af37] to-transparent",
      vert: "bottom-0 right-0 h-full w-[2px] bg-gradient-to-t from-[#d4af37] to-transparent",
    },
  };

  const c = config[position];
  if (!c) return null;

  return (
    <div key={position} className={`absolute ${c.container}`}>
      <div className={c.horiz} />
      <div className={c.vert} />
    </div>
  );
};

export default function Certificates() {
  const visibility = useSectionVisibility();
  const { data: certificates } = useCollectionData(
    "certificates",
    FALLBACK_CERTIFICATES
  );
  const [active, setActive] = useState(null);

  if (!visibility.certificates || !certificates.length) return null;

  const next = () =>
    setActive((i) => (i + 1) % certificates.length);
  const prev = () =>
    setActive((i) => (i - 1 + certificates.length) % certificates.length);

  return (
    <section
      id="certificates"
      data-testid="certificates-section"
      className="relative bg-[#0a0908] py-28 sm:py-36 border-t border-[#d4af37]/10 overflow-hidden"
    >
      {/* Subtle grain texture overlay */}
      <div className="absolute inset-0 grain pointer-events-none" />

      <div className="relative max-w-[1480px] mx-auto px-6 sm:px-10 lg:px-16">
        {/* ─── Header ─── */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
          >
            <div className="eyebrow mb-6 justify-center">
              Sertifikat & Akreditasi
            </div>
          </motion.div>
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1, duration: 0.8 }}
            className="font-serif text-4xl sm:text-5xl lg:text-6xl text-[#fdfbf7] font-light leading-[1.05] tracking-tight"
          >
            Terverifikasi &{" "}
            <span className="block italic text-[#d4af37]">diakui.</span>
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2, duration: 0.7 }}
            className="mt-5 text-sm text-[#a6a196] max-w-lg mx-auto leading-relaxed"
          >
            Setiap sertifikat adalah bukti dedikasi kami terhadap standar
            kecantikan profesional yang telah teruji dan diakui secara
            nasional.
          </motion.p>
        </div>

        {/* ─── Gallery Grid ─── */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 lg:gap-10 xl:gap-12">
          {certificates.map((cert, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ delay: i * 0.15, duration: 0.8 }}
              className="group"
            >
              <button
                type="button"
                onClick={() => setActive(i)}
                className="w-full text-left focus:outline-none focus-visible:ring-1 focus-visible:ring-[#d4af37]"
                aria-label={`Lihat sertifikat: ${cert.title}`}
              >
                {/* ─── Frame ─── */}
                <div className="relative">
                  {/* Outer glow — hidden by default, blooms on hover */}
                  <div className="absolute -inset-3 bg-gradient-to-b from-[#d4af37]/10 to-transparent opacity-0 group-hover:opacity-100 blur-2xl transition-opacity duration-700 rounded-sm pointer-events-none" />

                  {/* Outer frame border */}
                  <div
                    className="relative border border-[#d4af37]/30 p-[2px] bg-[#0a0908]
                               transition-all duration-500
                               group-hover:border-[#d4af37]/60
                               group-hover:shadow-[0_0_30px_-6px_rgba(212,175,55,0.2)]"
                  >
                    {/* Inner mat */}
                    <div className="relative bg-[#15120f] p-5 sm:p-8 overflow-hidden">
                      {/* ── Corner ornaments ── */}
                      {cornerOrnament("top-left")}
                      {cornerOrnament("top-right")}
                      {cornerOrnament("bottom-left")}
                      {cornerOrnament("bottom-right")}

                      {/* Gold sweep gradient overlay on hover */}
                      <div
                        className="absolute inset-0 opacity-0 group-hover:opacity-100
                                   transition-opacity duration-700 pointer-events-none
                                   bg-gradient-to-br from-[#d4af37]/8 via-[#d4af37]/3 to-transparent"
                      />

                      {/* Certificate image */}
                      <div className="relative aspect-[4/3] overflow-hidden bg-[#0a0908]">
                        <img
                          src={getCertImage(cert)}
                          alt={cert.title || `Sertifikat ${i + 1}`}
                          className="w-full h-full object-contain transition-all duration-700
                                     group-hover:scale-105 group-hover:brightness-110"
                        />

                        {/* Verification seal — slides in on hover */}
                        <div
                          className="absolute bottom-3 right-3 flex items-center gap-1.5
                                     bg-[#0a0908]/80 backdrop-blur-sm border border-[#d4af37]/30
                                     px-2.5 py-1.5
                                     opacity-0 translate-y-2
                                     group-hover:opacity-100 group-hover:translate-y-0
                                     transition-all duration-500"
                        >
                          <BadgeCheck size={12} className="text-[#d4af37]" />
                          <span className="text-[8px] tracking-[0.2em] uppercase text-[#d4af37] font-medium">
                            Tervalidasi
                          </span>
                        </div>

                        {/* "Click to inspect" hint */}
                        <div
                          className="absolute inset-0 flex items-center justify-center
                                     bg-[#0a0908]/0 group-hover:bg-[#0a0908]/20
                                     transition-colors duration-500"
                        >
                          <span
                            className="text-[10px] tracking-[0.3em] uppercase
                                       text-[#fdfbf7]/0 group-hover:text-[#fdfbf7]/70
                                       translate-y-2 group-hover:translate-y-0
                                       transition-all duration-500"
                          >
                            Klik untuk detail
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* ─── Museum Plaque ─── */}
                <div className="mt-5 text-center relative">
                  {/* Gold divider with diamond accent */}
                  <div className="flex items-center justify-center gap-3 mb-3">
                    <span className="h-px w-8 bg-gradient-to-r from-transparent to-[#d4af37]/50" />
                    <Award size={12} className="text-[#d4af37]/60 shrink-0" />
                    <span className="h-px w-8 bg-gradient-to-l from-transparent to-[#d4af37]/50" />
                  </div>

                  <p className="text-[10px] tracking-[0.35em] uppercase text-[#d4af37]/80 mb-1.5 font-medium">
                    {cert.issuer || ""}
                  </p>
                  <h3 className="font-serif text-lg sm:text-xl text-[#fdfbf7] transition-colors duration-500 group-hover:text-[#d4af37]">
                    {cert.title || ""}
                  </h3>
                  {cert.date && (
                    <p className="text-[11px] text-[#a6a196] mt-1.5 font-sans tracking-wide">
                      {cert.date}
                    </p>
                  )}
                </div>
              </button>
            </motion.div>
          ))}
        </div>
      </div>

      {/* ─── Lightbox ─── */}
      <AnimatePresence>
        {active !== null && (
          <motion.div
            key="lightbox-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-[100] bg-[#0a0908]/95 backdrop-blur-md flex items-center justify-center p-4 sm:p-8"
            onClick={() => setActive(null)}
          >
            {/* Close */}
            <motion.button
              initial={{ opacity: 0, rotate: -90 }}
              animate={{ opacity: 1, rotate: 0 }}
              transition={{ delay: 0.2, duration: 0.4 }}
              type="button"
              onClick={() => setActive(null)}
              className="absolute top-6 right-6 w-12 h-12 border border-[#d4af37]/40 text-[#d4af37] hover:bg-[#d4af37] hover:text-[#0a0908] flex items-center justify-center transition-all duration-500 z-10"
              aria-label="Tutup"
            >
              <X size={20} />
            </motion.button>

            {/* Prev */}
            <motion.button
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.3, duration: 0.4 }}
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                prev();
              }}
              className="absolute left-4 sm:left-10 top-1/2 -translate-y-1/2 w-12 h-12 border border-[#d4af37]/30 text-[#d4af37] hover:bg-[#d4af37] hover:text-[#0a0908] flex items-center justify-center transition-all duration-300 z-10"
              aria-label="Sebelumnya"
            >
              <ChevronLeft size={20} />
            </motion.button>

            {/* Next */}
            <motion.button
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.3, duration: 0.4 }}
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                next();
              }}
              className="absolute right-4 sm:right-10 top-1/2 -translate-y-1/2 w-12 h-12 border border-[#d4af37]/30 text-[#d4af37] hover:bg-[#d4af37] hover:text-[#0a0908] flex items-center justify-center transition-all duration-300 z-10"
              aria-label="Berikutnya"
            >
              <ChevronRight size={20} />
            </motion.button>

            {/* Content */}
            <motion.div
              key={active}
              initial={{ opacity: 0, scale: 0.92 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.92 }}
              transition={{ duration: 0.5 }}
              className="relative max-w-4xl w-full max-h-[85vh] flex flex-col items-center"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Framed certificate */}
              <div className="relative w-full max-h-[70vh] flex items-center justify-center bg-[#15120f] border border-[#d4af37]/20 p-4 sm:p-10">
                {/* Corner ornaments (lightbox) */}
                <div className="absolute top-0 left-0 w-6 h-6 sm:w-8 sm:h-8">
                  <div className="absolute top-0 left-0 w-full h-[1px] bg-gradient-to-r from-[#d4af37] to-transparent" />
                  <div className="absolute top-0 left-0 h-full w-[1px] bg-gradient-to-b from-[#d4af37] to-transparent" />
                </div>
                <div className="absolute top-0 right-0 w-6 h-6 sm:w-8 sm:h-8">
                  <div className="absolute top-0 right-0 w-full h-[1px] bg-gradient-to-l from-[#d4af37] to-transparent" />
                  <div className="absolute top-0 right-0 h-full w-[1px] bg-gradient-to-b from-[#d4af37] to-transparent" />
                </div>
                <div className="absolute bottom-0 left-0 w-6 h-6 sm:w-8 sm:h-8">
                  <div className="absolute bottom-0 left-0 w-full h-[1px] bg-gradient-to-r from-[#d4af37] to-transparent" />
                  <div className="absolute bottom-0 left-0 h-full w-[1px] bg-gradient-to-t from-[#d4af37] to-transparent" />
                </div>
                <div className="absolute bottom-0 right-0 w-6 h-6 sm:w-8 sm:h-8">
                  <div className="absolute bottom-0 right-0 w-full h-[1px] bg-gradient-to-l from-[#d4af37] to-transparent" />
                  <div className="absolute bottom-0 right-0 h-full w-[1px] bg-gradient-to-t from-[#d4af37] to-transparent" />
                </div>

                <img
                  src={getCertImage(certificates[active])}
                  alt={
                    certificates[active].title || `Sertifikat ${active + 1}`
                  }
                  className="max-h-[62vh] w-auto object-contain drop-shadow-2xl"
                />
              </div>

              {/* Caption */}
              <div className="mt-6 text-center">
                <p className="text-[10px] tracking-[0.35em] uppercase text-[#d4af37] mb-2">
                  {certificates[active].issuer || ""}
                </p>
                <h3 className="font-serif text-2xl sm:text-3xl text-[#fdfbf7]">
                  {certificates[active].title || ""}
                </h3>
                {certificates[active].date && (
                  <p className="text-sm text-[#a6a196] mt-1">
                    {certificates[active].date}
                  </p>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
